import React, { useState } from "react";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Modal,
  Autocomplete,
} from "@mui/material";
import { Plus, Trash2, Edit, X, Info } from "lucide-react";
import ProductSection from "../ProductSection";

// Mock data - would be fetched from API in real implementation
const COLORS = [
  { value: "Black", label: "Black", color: "#000000" },
  { value: "White", label: "White", color: "#FFFFFF" },
  { value: "Red", label: "Red", color: "#FF0000" },
  { value: "Blue", label: "Blue", color: "#0000FF" },
  { value: "Green", label: "Green", color: "#00FF00" },
  { value: "Yellow", label: "Yellow", color: "#FFFF00" },
  { value: "Brown", label: "Brown", color: "#A52A2A" },
];

const SIZES = ["6", "7", "8", "9", "10", "11", "12"];

const SellAsConfiguration = ({ productData, updateProductData, errors }) => {
  const [showAddPackModal, setShowAddPackModal] = useState(false);
  const [editingPackIndex, setEditingPackIndex] = useState(null);
  const [packData, setPackData] = useState({
    name: "",
    description: "",
    items: [],
  });
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [itemData, setItemData] = useState({
    color: "",
    size: "",
    price: 0,
    quantity: 1,
  });

  const handleSellAsChange = event => {
    updateProductData({
      sellAs: event.target.value,
    });
  };

  const handlePackChange = e => {
    const { name, value } = e.target;
    setPackData({
      ...packData,
      [name]: value,
    });
  };

  const handleItemChange = e => {
    const { name, value } = e.target;
    setItemData({
      ...itemData,
      [name]:
        name === "price" || name === "quantity"
          ? parseFloat(value) || 0
          : value,
    });
  };

  const handleColorChange = (_, value) => {
    setItemData({
      ...itemData,
      color: value ? value.value : "",
    });
  };

  const handleAddItem = () => {
    if (editingItemIndex !== null) {
      // Update existing item
      const updatedItems = [...packData.items];
      updatedItems[editingItemIndex] = itemData;
      setPackData({
        ...packData,
        items: updatedItems,
      });
      setEditingItemIndex(null);
    } else {
      // Add new item
      setPackData({
        ...packData,
        items: [...packData.items, itemData],
      });
    }

    // Reset form
    setItemData({
      color: "",
      size: "",
      price: 0,
      quantity: 1,
    });
    setShowAddItemModal(false);
  };

  const handleEditItem = index => {
    setItemData(packData.items[index]);
    setEditingItemIndex(index);
    setShowAddItemModal(true);
  };

  const handleDeleteItem = index => {
    const updatedItems = [...packData.items];
    updatedItems.splice(index, 1);
    setPackData({
      ...packData,
      items: updatedItems,
    });
  };

  const handleAddPack = () => {
    if (editingPackIndex !== null) {
      // Update existing pack
      const updatedPacks = [...productData.sellAsPacks];
      updatedPacks[editingPackIndex] = packData;
      updateProductData({ sellAsPacks: updatedPacks });
      setEditingPackIndex(null);
    } else {
      // Add new pack
      updateProductData({
        sellAsPacks: [...productData.sellAsPacks, packData],
      });
    }

    // Reset form
    setPackData({
      name: "",
      description: "",
      items: [],
    });
    setShowAddPackModal(false);
  };

  const handleEditPack = index => {
    setPackData(productData.sellAsPacks[index]);
    setEditingPackIndex(index);
    setShowAddPackModal(true);
  };

  const handleDeletePack = index => {
    const updatedPacks = [...productData.sellAsPacks];
    updatedPacks.splice(index, 1);
    updateProductData({ sellAsPacks: updatedPacks });
  };

  const calculatePackTotal = items => {
    return items.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const calculatePackItems = items => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <div className="space-y-8">
      <ProductSection data="Sell As Configuration" />

      <div className="bg-blue-50 p-4 rounded-lg mb-6">
        <p className="text-blue-700 font-outfit text-sm flex items-start">
          <Info size={16} className="mr-2 mt-0.5 flex-shrink-0" />
          <span>
            Configure how your product can be sold. You can sell it as
            individual units (PRIMARY), as packs (SECONDARY), as cartons
            (TERTIARY), or a mix of these options (MIXED).
          </span>
        </p>
      </div>

      <FormControl component="fieldset">
        <RadioGroup
          name="sellAs"
          value={productData.sellAs}
          onChange={handleSellAsChange}>
          <FormControlLabel
            value="PRIMARY"
            control={
              <Radio
                sx={{ color: "#5652B0", "&.Mui-checked": { color: "#5652B0" } }}
              />
            }
            label={
              <span className="font-outfit">Sell as Primary Units Only</span>
            }
          />
          <FormControlLabel
            value="SECONDARY"
            control={
              <Radio
                sx={{ color: "#5652B0", "&.Mui-checked": { color: "#5652B0" } }}
              />
            }
            label={
              <span className="font-outfit">
                Sell as Secondary Units (Packs)
              </span>
            }
          />
          <FormControlLabel
            value="TERTIARY"
            control={
              <Radio
                sx={{ color: "#5652B0", "&.Mui-checked": { color: "#5652B0" } }}
              />
            }
            label={
              <span className="font-outfit">
                Sell as Tertiary Units (Cartons)
              </span>
            }
          />
          <FormControlLabel
            value="MIXED"
            control={
              <Radio
                sx={{ color: "#5652B0", "&.Mui-checked": { color: "#5652B0" } }}
              />
            }
            label={
              <span className="font-outfit">
                Mixed (Sell in Multiple Unit Types)
              </span>
            }
          />
        </RadioGroup>
      </FormControl>

      {(productData.sellAs === "SECONDARY" ||
        productData.sellAs === "TERTIARY" ||
        productData.sellAs === "MIXED") && (
        <div className="space-y-6 mt-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium font-outfit">
              {productData.sellAs === "SECONDARY"
                ? "Pack Configurations"
                : productData.sellAs === "TERTIARY"
                ? "Carton Configurations"
                : "Pack/Carton Configurations"}
            </h3>

            <button
              onClick={() => {
                setShowAddPackModal(true);
                setEditingPackIndex(null);
                setPackData({
                  name: "",
                  description: "",
                  items: [],
                });
              }}
              className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] flex items-center gap-2">
              <Plus size={16} />
              Add{" "}
              {productData.sellAs === "SECONDARY"
                ? "Pack"
                : productData.sellAs === "TERTIARY"
                ? "Carton"
                : "Configuration"}
            </button>
          </div>

          {productData.sellAsPacks.length === 0 ? (
            <div className="bg-gray-50 p-8 rounded-lg text-center">
              <p className="text-gray-500 mb-4">No configurations added yet</p>
              <button
                onClick={() => {
                  setShowAddPackModal(true);
                  setEditingPackIndex(null);
                }}
                className="px-4 py-2 border border-[#5652B0] text-[#5652B0] rounded-lg text-sm font-medium hover:bg-[#F9F8FE]">
                Add Your First Configuration
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {productData.sellAsPacks.map((pack, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 p-4 flex justify-between items-center">
                    <div>
                      <h4 className="font-medium font-outfit">{pack.name}</h4>
                      <p className="text-sm text-gray-500 font-outfit">
                        {pack.description}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <IconButton
                        size="small"
                        onClick={() => handleEditPack(index)}
                        sx={{ color: "#5652B0" }}>
                        <Edit size={16} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeletePack(index)}
                        sx={{ color: "#d32f2f" }}>
                        <Trash2 size={16} />
                      </IconButton>
                    </div>
                  </div>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
                            Color
                          </TableCell>
                          <TableCell
                            sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
                            Size
                          </TableCell>
                          <TableCell
                            sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
                            Qty
                          </TableCell>
                          <TableCell
                            sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
                            Price
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pack.items.map((item, itemIndex) => (
                          <TableRow key={itemIndex}>
                            <TableCell sx={{ fontFamily: "Outfit" }}>
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{
                                    backgroundColor:
                                      COLORS.find(c => c.value === item.color)
                                        ?.color || "#000",
                                  }}></div>
                                {item.color}
                              </div>
                            </TableCell>
                            <TableCell sx={{ fontFamily: "Outfit" }}>
                              {item.size}
                            </TableCell>
                            <TableCell sx={{ fontFamily: "Outfit" }}>
                              {item.quantity}
                            </TableCell>
                            <TableCell sx={{ fontFamily: "Outfit" }}>
                              ₹{item.price}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <div className="p-4 border-t border-gray-200">
                    <div className="flex justify-between">
                      <span className="font-outfit text-sm">Total Items:</span>
                      <span className="font-outfit font-medium">
                        {calculatePackItems(pack.items)}
                      </span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="font-outfit text-sm">Total Price:</span>
                      <span className="font-outfit font-medium">
                        ₹{calculatePackTotal(pack.items)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Pack Modal */}
      <Modal
        open={showAddPackModal}
        onClose={() => setShowAddPackModal(false)}
        aria-labelledby="add-pack-modal">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 800,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            maxHeight: "90vh",
            overflow: "auto",
          }}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold font-outfit">
              {editingPackIndex !== null ? "Edit" : "Add"}{" "}
              {productData.sellAs === "SECONDARY"
                ? "Pack"
                : productData.sellAs === "TERTIARY"
                ? "Carton"
                : "Configuration"}
            </h2>
            <IconButton onClick={() => setShowAddPackModal(false)}>
              <X size={20} />
            </IconButton>
          </div>

          <div className="space-y-6">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Name"
                  name="name"
                  value={packData.name}
                  onChange={handlePackChange}
                  fullWidth
                  size="small"
                  required
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Description"
                  name="description"
                  value={packData.description}
                  onChange={handlePackChange}
                  fullWidth
                  size="small"
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                    },
                  }}
                />
              </Grid>
            </Grid>

            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium font-outfit">Items</h3>

              <button
                onClick={() => {
                  setShowAddItemModal(true);
                  setEditingItemIndex(null);
                  setItemData({
                    color: "",
                    size: "",
                    price: 0,
                    quantity: 1,
                  });
                }}
                className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] flex items-center gap-2">
                <Plus size={16} />
                Add Item
              </button>
            </div>

            {packData.items.length === 0 ? (
              <div className="bg-gray-50 p-6 rounded-lg text-center">
                <p className="text-gray-500">No items added yet</p>
              </div>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: "0.5rem" }}>
                <Table>
                  <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
                        Color
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
                        Size
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
                        Price (₹)
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
                        Quantity
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
                        Total
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {packData.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontFamily: "Outfit" }}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{
                                backgroundColor:
                                  COLORS.find(c => c.value === item.color)
                                    ?.color || "#000",
                              }}></div>
                            {item.color}
                          </div>
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Outfit" }}>
                          {item.size}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Outfit" }}>
                          ₹{item.price}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Outfit" }}>
                          {item.quantity}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Outfit" }}>
                          ₹{item.price * item.quantity}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <IconButton
                              size="small"
                              onClick={() => handleEditItem(index)}
                              sx={{ color: "#5652B0" }}>
                              <Edit size={16} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteItem(index)}
                              sx={{ color: "#d32f2f" }}>
                              <Trash2 size={16} />
                            </IconButton>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
                        Total
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
                        {calculatePackItems(packData.items)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
                        ₹{calculatePackTotal(packData.items)}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={() => setShowAddPackModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleAddPack}
                disabled={!packData.name || packData.items.length === 0}
                className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] disabled:opacity-50 disabled:cursor-not-allowed">
                {editingPackIndex !== null ? "Update" : "Add"}{" "}
                {productData.sellAs === "SECONDARY"
                  ? "Pack"
                  : productData.sellAs === "TERTIARY"
                  ? "Carton"
                  : "Configuration"}
              </button>
            </div>
          </div>
        </Box>
      </Modal>

      {/* Add Item Modal */}
      <Modal
        open={showAddItemModal}
        onClose={() => setShowAddItemModal(false)}
        aria-labelledby="add-item-modal">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold font-outfit">
              {editingItemIndex !== null ? "Edit" : "Add"} Item
            </h2>
            <IconButton onClick={() => setShowAddItemModal(false)}>
              <X size={20} />
            </IconButton>
          </div>

          <div className="space-y-4">
            <Autocomplete
              options={COLORS}
              getOptionLabel={option => option.label}
              value={COLORS.find(c => c.value === itemData.color) || null}
              onChange={handleColorChange}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      backgroundColor: option.color,
                      border: "1px solid #E5E7EB",
                    }}
                  />
                  {option.label}
                </Box>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Color"
                  required
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                    },
                  }}
                />
              )}
            />

            <TextField
              select
              label="Size"
              name="size"
              value={itemData.size}
              onChange={handleItemChange}
              fullWidth
              size="small"
              required
              SelectProps={{
                native: true,
              }}
              InputProps={{
                sx: {
                  borderRadius: "0.5rem",
                  fontFamily: "Outfit",
                },
              }}
              InputLabelProps={{
                sx: {
                  fontFamily: "Outfit",
                },
              }}>
              <option value=""></option>
              {SIZES.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </TextField>

            <TextField
              label="Price (₹)"
              name="price"
              type="number"
              value={itemData.price}
              onChange={handleItemChange}
              fullWidth
              size="small"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
                sx: {
                  borderRadius: "0.5rem",
                  fontFamily: "Outfit",
                },
              }}
              InputLabelProps={{
                sx: {
                  fontFamily: "Outfit",
                },
              }}
            />

            <TextField
              label="Quantity"
              name="quantity"
              type="number"
              value={itemData.quantity}
              onChange={handleItemChange}
              fullWidth
              size="small"
              required
              InputProps={{
                sx: {
                  borderRadius: "0.5rem",
                  fontFamily: "Outfit",
                },
              }}
              InputLabelProps={{
                sx: {
                  fontFamily: "Outfit",
                },
              }}
            />

            <div className="flex justify-end gap-3 pt-4 border-t mt-4">
              <button
                onClick={() => setShowAddItemModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleAddItem}
                disabled={
                  !itemData.color ||
                  !itemData.size ||
                  itemData.price <= 0 ||
                  itemData.quantity <= 0
                }
                className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] disabled:opacity-50 disabled:cursor-not-allowed">
                {editingItemIndex !== null ? "Update" : "Add"} Item
              </button>
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default SellAsConfiguration;
